import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import ReactMarkdown from 'react-markdown';
import { useApiKey } from '../context/ApiKeyContext';
import { generateFeedback } from '../utils/anthropicService';

const Container = styled.div`
  padding: 2rem;
  background-color: white;
  border-radius: 10px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  max-width: 800px;
  width: 100%;
`;

const Title = styled.h2`
  color: #e91e63;
  margin-bottom: 1.5rem;
  text-align: center;
`;

const Subtitle = styled.h3`
  color: #333;
  margin-top: 2rem;
  margin-bottom: 1rem;
  font-size: 1.2rem;
`;

const FeedbackContent = styled.div`
  background-color: #f9f9f9;
  padding: 1.5rem;
  border-radius: 8px;
  margin-bottom: 2rem;
  line-height: 1.6;

  > *:first-child {
    margin-top: 0;
  }

  > *:last-child {
    margin-bottom: 0;
  }

  p, ul, ol, blockquote, pre {
    margin: 0 0 1rem;
  }

  h1, h2, h3, h4, h5, h6 {
    margin: 2rem 0 1rem;
  }

  hr {
    margin: 1.5rem 0;
    border: none;
    border-top: 1px solid #ddd;
  }

  ul, ol {
    padding-left: 1.5rem;
  }

  li {
    margin-bottom: 0.25rem;
  }

  code {
    background-color: #eee;
    padding: 0.15rem 0.3rem;
    border-radius: 3px;
    font-size: 0.9em;
  }
`;

const ConversationPreview = styled.div`
  max-height: 300px;
  overflow-y: auto;
  margin-bottom: 2rem;
  border: 1px solid #eee;
  border-radius: 8px;
  padding: 1rem;
`;

const MessageItem = styled.div`
  margin-bottom: 1rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #eee;
  &:last-child {
    border-bottom: none;
    margin-bottom: 0;
    padding-bottom: 0;
  }
`;

const MessageHeader = styled.div`
  font-weight: 600;
  color: ${({ isUser }) => (isUser ? '#e91e63' : '#333')};
  margin-bottom: 0.5rem;
`;

const MessageContent = styled.div`
  margin-left: 1rem;
  color: #555;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 1rem;
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  background-color: ${({ $secondary }) => ($secondary ? 'white' : '#e91e63')};
  color: ${({ $secondary }) => ($secondary ? '#e91e63' : 'white')};
  border: ${({ $secondary }) => ($secondary ? '1px solid #e91e63' : 'none')};
  border-radius: 4px;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background-color: ${({ $secondary }) => ($secondary ? '#fff5f8' : '#d81b60')};
  }
`;

const LoadingText = styled.div`
  text-align: center;
  color: #666;
  padding: 2rem;
  font-style: italic;
`;

const GameResultBanner = styled.div`
  text-align: center;
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1.5rem;
  font-weight: 600;
  background-color: ${({ won }) => (won ? '#e8f5e9' : '#fdecea')};
  color: ${({ won }) => (won ? '#2e7d32' : '#c62828')};
`;

const FeedbackDisplay = ({ conversation, character, personality, gameMode, gameResult, conversationId, onStartOver, onTryAgain, onTrialBlocked }) => {
  const { apiKey, trialMode } = useApiKey();
  const [feedback, setFeedback] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const auth = trialMode ? { trial: true } : { apiKey };
    const fetchFeedback = async () => {
      try {
        setIsLoading(true);
        const result = await generateFeedback(auth, conversation, gameMode, { conversationId });

        if (result.success) {
          setFeedback(result.feedback);
        } else if ((result.code === 'owner_out_of_credits' || result.code === 'trial_exhausted') && onTrialBlocked) {
          onTrialBlocked(result.code);
        } else {
          setFeedback('Unable to generate feedback at this time. Please try again later.');
        }
      } catch (error) {
        console.error('Error generating feedback:', error);
        setFeedback('An error occurred while generating feedback. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchFeedback();
  }, [apiKey, trialMode, conversation, gameMode, conversationId, onTrialBlocked]);

  return (
    <Container>
      <Title>Dating Practice Feedback</Title>

      {gameMode && (
        <GameResultBanner won={gameResult === 'won'}>
          {gameResult === 'won'
            ? `You got the date! ${character.name} said yes.`
            : `Time's up — you didn't land the date with ${character.name} this time.`}
        </GameResultBanner>
      )}

      <Subtitle>Your Conversation with {character.name} ({personality.name} Type)</Subtitle>
      <ConversationPreview>
        {conversation.map((message, index) => (
          <MessageItem key={index}>
            <MessageHeader isUser={message.role === 'user'}>
              {message.role === 'user' ? 'You' : character.name}
            </MessageHeader>
            <MessageContent>{message.content}</MessageContent>
          </MessageItem>
        ))}
      </ConversationPreview>

      {isLoading ? (
        <LoadingText>Analyzing your conversation and generating feedback...</LoadingText>
      ) : (
        <FeedbackContent>
          <ReactMarkdown>{feedback}</ReactMarkdown>
        </FeedbackContent>
      )}

      <ButtonContainer>
        <Button onClick={onStartOver} $secondary={true}>
          Start Over
        </Button>
        <Button onClick={onTryAgain}>
          Try Again with {character.name}
        </Button>
      </ButtonContainer>
    </Container>
  );
};

export default FeedbackDisplay;
